import {
  AdminScheduleType,
  AdminScheduleUserImport
} from '@dadohr/shared/domainTypes/adminSchedules';
import { Compressed } from '@dadohr/shared/domainTypes/compression';
import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  DiffSet,
  IUser,
  IUserImportEffects,
  IUserImportMeta,
  IUserImportMetaOptions,
  UserChangeSet,
  UserImportDataSource,
  UserImportDataSourceCSV,
  UserImportDataSourceIdentifier,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType,
  UserImportStatus
} from '@dadohr/shared/domainTypes/user';
import { CF, FS } from '@dadohr/shared/versions';
import { assertNever } from 'assert-never';
import { sortBy } from 'lodash';
import { useEffect } from 'react';
import { useCollection, useDocument } from 'react-firebase-hooks/firestore';
import shortid from 'shortid';
import {
  IMPORT_META,
  MergeHRISIntegrationsInfoMap,
} from '../../features/Users/pages/Imports/components/sources/meta';
import { usePromise } from '../../hooks/usePromise';
import { toAdminScheduleDoc } from '../adminSchedules';
import { decompressType } from '../compression';
import { getCurrentUser } from '../currentUser';
import { downloadFile } from '../file';
import {
  batchUpdateDocsFromPartials,
  refreshTimestamp,
  store,
  useMappedLoadingValue
} from '../firebase';
import { callFirebaseFunction } from '../firebaseFunctions';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../firecache/documentListener';
import { now } from '../time';
import { refreshUsersByOrgId } from './index';

export const toUserImportMetaDoc = generateToDocFn<IUserImportMeta>((d) => {
  d.stats.effects = d.stats.effects || 0;
  d.errors = d.errors || [];
  return d;
});
export const toUserImportEffectsDoc = generateToDocFn<IUserImportEffects>(
  (d) => {
    d.effects = d.effects || [];
    return d;
  }
);

// The org user set is a one-shot fetch (not a live listener), so anything that
// writes users must refresh it. Passes the callable's result through.
const refreshUsersAfter =
  (orgId: string) =>
  <R>(result: R): R => {
    refreshUsersByOrgId(orgId);
    return result;
  };

const TERMINAL_IMPORT_STATUSES = new Set<string>([
  UserImportStatus.DONE,
  UserImportStatus.PARTIAL_FAILURE,
  UserImportStatus.ERROR
]);
// Last status seen per import id this session.
const seenImportStatus = new Map<string, string>();

/**
 * Imports (HRIS sync, CSV with auto-apply) write users on the backend AFTER the
 * starting callable returns. While an import is being watched, refresh the
 * org's user set when it transitions INTO a terminal status. Imports already
 * terminal when first seen don't trigger a refresh.
 */
const refreshUsersOnImportFinish = (
  docs: (Doc<IUserImportMeta> | null | undefined | void)[]
) => {
  docs.forEach((d) => {
    if (!d) return;
    const status = String(d.data.status);
    const prev = seenImportStatus.get(d.id);
    seenImportStatus.set(d.id, status);
    if (prev !== undefined && prev !== status && TERMINAL_IMPORT_STATUSES.has(status)) {
      refreshUsersByOrgId(d.data.orgId);
    }
  });
};

export const startImport = (
  orgId: string,
  sources: UserImportDataSourceIdentifier[],
  options: IUserImportMetaOptions
) => {
  return callFirebaseFunction<Doc<IUserImportMeta>>(CF.userImport.startImport, {
    orgId,
    sources,
    options
  }).then(refreshUsersAfter(orgId));
};

const EMPTY_CSV_TEMPLATE = `
Work email,First name,Last name,Role,Department,Location,Add your own extra fields as new columns
example@example.com,Max,Musterman,Example job title,Example dept,Example location,
`.trim();

export const downloadEmptyCSVImportTemplate = () => {
  downloadFile('Dado - Employee List', 'csv', EMPTY_CSV_TEMPLATE);
};

type CSVImportResponse =
  | {
      status: 'ERROR';
      errorMessage: string;
    }
  | {
      status: 'OK';
      importDocId: string;
    };
export const startCSVImport = (
  orgId: string,
  options: IUserImportMetaOptions,
  storageRef: string
) => {
  return callFirebaseFunction<CSVImportResponse>(CF.userImport.startCSVImport, {
    orgId,
    options,
    storageRef
  }).then(refreshUsersAfter(orgId));
};

export const downloadCurrentCSV = (orgId: string) => {
  return callFirebaseFunction<string>(CF.userImport.downloadCurrentCSV, {
    orgId
  });
};

export const updateCSVDataSource = ({
  orgId,
  storageRef
}: {
  orgId: string;
  storageRef: string;
}) => {
  return callFirebaseFunction<{
    status: 'OK' | 'ERROR';
    errorMessage?: string;
  }>(CF.userImport.updateCSVDataSource, {
    orgId,
    storageRef
  });
};

export const commitImport = (orgId: string, importId: string) => {
  return callFirebaseFunction(CF.userImport.commitImport, {
    orgId,
    importId
  }).then(refreshUsersAfter(orgId));
};

export const importByIdStore = createDocumentListenerGetter(
  (id) => store().collection(FS.userImports).doc(id),
  toUserImportMetaDoc
);

export const importsByOrgIdStore = createCollectionListenerStore(
  (orgId: string) =>
    new CollectionListener(
      store().collection(FS.userImports).where('orgId', '==', orgId),
      toUserImportMetaDoc
    )
);

export const useImportById = (id: string) => {
  const v = useDocumentListener(importByIdStore(id));
  useEffect(() => {
    refreshUsersOnImportFinish([v[0]]);
  }, [v[0]]);
  return v;
};

export const useImportsByOrgId = (orgId: string) => {
  const v = useCollectionListener(importsByOrgIdStore(orgId));
  useEffect(() => {
    if (v[0]) refreshUsersOnImportFinish(v[0]);
  }, [v[0]]);
  return v;
};

const diffSetCache: { [key: string]: Promise<DiffSet> } = {};

const refreshUserTimestamps = (d: Doc<IUser>) => {
  // We need to refresh custom fields if they contain dates!
  // Refresh timestamp is quite resilient - we should be able to get
  // away with calling it on every field
  d.data.hireDate = refreshTimestamp(d.data.hireDate);
  d.data.terminationDate = refreshTimestamp(d.data.terminationDate);
  d.data.dateOfBirth = refreshTimestamp(d.data.dateOfBirth);
  d.data.customFields = d.data.customFields || {};
  for (const k in d.data.customFields) {
    d.data.customFields[k] = refreshTimestamp(d.data.customFields[k]);
  }
};

const refreshUserChanges = (d: UserChangeSet) => {
  // We need to refresh custom fields if they contain dates!
  // Refresh timestamp is quite resilient - we should be able to get
  // away with calling it on every field
  for (const k in d) {
    const v = d[k];
    if (v) {
      v.bef = refreshTimestamp(v.bef);
      v.aft = refreshTimestamp(v.aft);
    }
  }
};

const refreshDiffSet = (diffSet: DiffSet) => {
  diffSet.added.forEach((c) => {
    refreshUserTimestamps(c);
  });
  diffSet.removed.forEach((c) => {
    refreshUserTimestamps(c);
  });
  diffSet.changed.forEach((c) => {
    refreshUserTimestamps(c.bef);
    refreshUserTimestamps(c.aft);
    refreshUserChanges(c.changes);
  });
};

export const getImportDiffSet = (orgId: string, importId: string) => {
  const key = [orgId, importId].join('-');
  return (diffSetCache[key] =
    diffSetCache[key] ||
    callFirebaseFunction<Compressed<DiffSet>>(CF.userImport.getImportDiffSet, {
      orgId,
      importId
    }).then((diffSetCompressed) => {
      const diffSet = decompressType(diffSetCompressed);
      refreshDiffSet(diffSet);
      return diffSet;
    }));
};

export const EMPTY_DIFF_SET: DiffSet = {
  added: [],
  changed: [],
  removed: [],
  issues: []
};

export const useImportDiffset = (d: Doc<IUserImportMeta>) => {
  return usePromise(async () => {
    if (
      d.data.status === UserImportStatus.PREVIEW ||
      d.data.status === UserImportStatus.DONE ||
      d.data.status === UserImportStatus.PARTIAL_FAILURE
    ) {
      return getImportDiffSet(d.data.orgId, d.id);
    }
    return EMPTY_DIFF_SET;
  }, [d]);
};

export const useImportEffects = (importId: string) => {
  return useMappedLoadingValue(
    useDocument(store().collection(FS.userImportEffects).doc(importId)),
    (s) => (s.exists ? toUserImportEffectsDoc(s) : null)
  );
};

const toUserImportDataSourceDoc = generateToDocFn<UserImportDataSource>((d) => {
  d.customMappings = d.customMappings || [];
  if (d.type === UserImportSourceType.AIRTABLE) {
    d.name = d.name || '';
  }
  if (d.type === UserImportSourceType.WORKDAY_REPORT_API) {
    d.dataShapeStyle = d.dataShapeStyle || 'HRIS';
  }
  if (d.type === UserImportSourceType.GREENHOUSE_V3) {
    d.offerStatusToFilterBy = d.offerStatusToFilterBy || 'Accepted';
  }
  if (d.type === UserImportSourceType.SFTP) {
    d.dateFormats = d.dateFormats || []; // TODO set a reasonable default
  }
  return d;
});

const toUserImportDataSourceCSVDoc = generateToDocFn<UserImportDataSourceCSV>(
  (d) => {
    d.customMappings = d.customMappings || [];
    return d;
  }
);

const userImportDataSourcesByOrgIdStore = createCollectionListenerStore(
  (orgId) =>
    new CollectionListener(
      store().collection(FS.userImportDataSources).where('orgId', '==', orgId),
      toUserImportDataSourceDoc
    )
);

export const useUserImportDataSources = (orgId: string) => {
  return useCollectionListener(userImportDataSourcesByOrgIdStore(orgId));
};

export const useCSVImportSource = (orgId: string) => {
  return useMappedLoadingValue(
    useUserImportDataSources(orgId),
    (importSources) => {
      const csvImportSource = importSources.filter(
        (s) => s.data.type === UserImportSourceType.MANUAL_CSV
      )[0];
      return csvImportSource;
    }
  );
};

export const updateImportDataSourceInactiveStatus = async (
  d: Doc<UserImportDataSource>,
  isInactive: boolean,
  options?: {
    reason?: string;
    userId?: string;
  }
): Promise<void> => {
  const oldStatus = d.data.isInactive;
  const newStatus = isInactive;

  if (oldStatus === newStatus) {
    return;
  }
  const userId = options?.userId || getCurrentUser().id;

  store()
    .collection(FS.userImportDataSources)
    .doc(d.id)
    .update({
      isInactive,
      updatedAt: now(), // could we set this somewhere with a generic update function?
      lastStatusChange: {
        changedAt: now(),
        changedBy: userId,
        previouslyInactive: oldStatus ?? false,
        reason: options?.reason || ''
      }
    });
};

export const updateImportDataSourcesOrder = async (
  dataSources: Doc<UserImportDataSource>[],
  sourceIndex: number,
  destinationIndex: number,
  options?: {
    reason?: string;
    userId?: string;
  }
): Promise<void> => {
  const userId = options?.userId || getCurrentUser().id;
  const reorderedSources = [...dataSources];
  const [removedSource] = reorderedSources.splice(sourceIndex, 1);
  reorderedSources.splice(destinationIndex, 0, removedSource);

  const docsToUpdate: Doc<any>[] = reorderedSources.map((source, index) => ({
    id: source.id,
    collection: source.collection,
    data: {
      importOrder: index,
      lastStatusChange: {
        changedAt: now(),
        changedBy: userId,
        previousImportOrder: source.data.importOrder ?? null,
        reason: options?.reason || ''
      }
    }
  }));

  // Get the orgId from any source (they should all be the same)
  const orgId = dataSources[0]?.data.orgId;

  if (orgId) {
    const orderMap: Record<string, number> = {};
    reorderedSources.forEach((source, index) => {
      orderMap[source.id] = index;
    });

    const scheduleQuery = await store()
      .collection(FS.adminSchedules)
      .where('orgId', '==', orgId)
      .where('type', '==', AdminScheduleType.USER_IMPORT)
      .get()
      .then((s) => s.docs.map(toAdminScheduleDoc));

    if (scheduleQuery?.length) {
      const scheduleDoc = scheduleQuery[0] as Doc<AdminScheduleUserImport>;

      const sortedSources = sortBy(scheduleDoc.data.sources, (s) => {
        return orderMap ? orderMap[s.id] : Number.MAX_SAFE_INTEGER;
      });

      docsToUpdate.push({
        id: scheduleDoc.id,
        collection: FS.adminSchedules,
        data: {
          sources: sortedSources,
          orderMap
        }
      });
    }
  }

  if (docsToUpdate.length > 0) {
    await batchUpdateDocsFromPartials(docsToUpdate);
  }

  return Promise.resolve();
};

const DEFAULT_CSV_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: 'email',
    otherKey: 'Work Email'
  },
  {
    dadoKey: 'firstName',
    otherKey: 'First Name'
  },
  {
    dadoKey: 'lastName',
    otherKey: 'Last Name'
  },
  {
    dadoKey: 'location',
    otherKey: 'Location'
  },
  {
    dadoKey: 'role',
    otherKey: 'Role'
  },
  {
    dadoKey: 'department',
    otherKey: 'Department'
  }
];

export const getEmptyCSVImportSource = (orgId: string, userId: string) => {
  const csvDatadource: UserImportDataSourceCSV = {
    type: UserImportSourceType.MANUAL_CSV,
    status: UserImportDataSourceStatus.PENDING,
    detailedStatus: '',
    lastUpdate: null,
    createdBy: userId,
    createdAt: now(),
    customMappings: [...DEFAULT_CSV_MAPPINGS],
    orgId,
    storageRef: '',
    importRecord: []
  };
  const datasourceDoc: Doc<UserImportDataSourceCSV> = {
    id: shortid(),
    collection: FS.userImportDataSources,
    data: csvDatadource
  };
  return datasourceDoc;
};

const getCSVUserImportSourceQuery = (orgId: string) => {
  const typeKey: keyof UserImportDataSource = 'type';
  return store()
    .collection(FS.userImportDataSources)
    .where('orgId', '==', orgId)
    .where(typeKey, '==', UserImportSourceType.MANUAL_CSV);
};

export const getCSVUserImportSource = (orgId: string) => {
  return getCSVUserImportSourceQuery(orgId)
    .get()
    .then((s) => {
      const d = s.docs[0];
      return d ? toUserImportDataSourceDoc(d) : null;
    });
};

export const useCSVUserImportSourceOrGetEmptyDefault = (
  orgId: string,
  userId: string
) => {
  const query = getCSVUserImportSourceQuery(orgId);
  return useMappedLoadingValue(useCollection(query), (queryResult) => {
    const d = queryResult.docs[0];
    return d
      ? toUserImportDataSourceCSVDoc(d)
      : getEmptyCSVImportSource(orgId, userId);
  });
};

export const toUserImportSourceIdentifier = (
  d: Doc<UserImportDataSource>
): UserImportDataSourceIdentifier => {
  if (d.data.type === UserImportSourceType.MERGE_HRIS) {
    return {
      id: d.id,
      type: d.data.type,
      isInactive: d.data.isInactive || false,
      provider: d.data.hris
    };
  }
  return {
    id: d.id,
    type: d.data.type,
    isInactive: d.data.isInactive || false
  };
};

// Greenhouse v1 was removed in Sept 2026, but import runs are immutable — old
// runs still carry this source type, and the switch below ends in assertNever.
const LEGACY_GREENHOUSE_V1_TYPE = 'GREENHOUSE';

export const getUserImportSourceNameFromIdentifier = (
  d: UserImportDataSourceIdentifier
) => {
  if ((d.type as string) === LEGACY_GREENHOUSE_V1_TYPE) {
    return 'Greenhouse';
  }

  switch (d.type) {
    case UserImportSourceType.AIRTABLE:
      return IMPORT_META.AIRTABLE.name;
    case UserImportSourceType.BAMBOO_HR:
      return IMPORT_META.BAMBOOHR.name;
    case UserImportSourceType.BOB:
      return IMPORT_META.BOB.name;
    case UserImportSourceType.MANUAL_CSV:
      return IMPORT_META.CSV.name;
    case UserImportSourceType.GOOGLE_SHEET:
      return IMPORT_META.GOOGLE.name;
    case UserImportSourceType.GREENHOUSE_V3:
      return IMPORT_META.GREENHOUSE_V3.name;
    case UserImportSourceType.MS_EXCEL:
      return IMPORT_META.EXCEL.name;
    case UserImportSourceType.SFTP:
      return IMPORT_META.SFTP.name;
    case UserImportSourceType.WORKDAY_REPORT_API:
      return IMPORT_META.WORKDAY.name;
    case UserImportSourceType.MERGE_HRIS:
      return MergeHRISIntegrationsInfoMap[d.provider]?.name || 'Merge';
    case UserImportSourceType.CHARTHOP:
      return IMPORT_META.CHARTHOP.name;
    case UserImportSourceType.PINGBOARD:
      return IMPORT_META.PINGBOARD.name;
    case UserImportSourceType.ADP_WORKFORCE_NOW:
      return IMPORT_META.ADP_WORKFORCE_NOW.name;
    case UserImportSourceType.ASHBY:
      return IMPORT_META.ASHBY.name;
    case UserImportSourceType.PERSONIO:
      return IMPORT_META.PERSONIO.name;
    case UserImportSourceType.EMAIL_CSV:
      return IMPORT_META.EMAIL_CSV.name;
    case UserImportSourceType.SALESFORCE:
      return IMPORT_META.SALESFORCE.name;
    case UserImportSourceType.UKG:
      return IMPORT_META.UKG.name;

    default:
      return assertNever(d);
  }
};

export const getUserImportDataSourceName = (d: Doc<UserImportDataSource>) => {
  const name =
    d.data.type === UserImportSourceType.AIRTABLE
      ? d.data.name
      : d.data.type === UserImportSourceType.GOOGLE_SHEET
      ? d.data.name
      : '';
  if (name) {
    return name;
  }
  const identifier = toUserImportSourceIdentifier(d);
  return getUserImportSourceNameFromIdentifier(identifier);
};

export const getUserImportDataSourceTypeName = (
  d: Doc<UserImportDataSource>
) => {
  const identifier = toUserImportSourceIdentifier(d);
  return getUserImportSourceNameFromIdentifier(identifier);
};
